// Pure helpers for the ED document assistant (EdDocumentAssistant.tsx). No
// React, no imports — tested by frontend/tests/edDocs.test.mjs.
//
// One conversation per ED encounter, several documents. The AI decides per
// message whether to answer or to write a document; when it writes one, its
// reply is:
//
//   <a sentence or two to the doctor>
//   --- AI-ASSISTED DRAFT (Doctor approval required) ---
//   Document: Referral Letter
//   <the document>
//
// The "Document:" line names which tab the document goes into — the server's
// ED prompt (backend/utils/erDocuments.js#buildEdSystemPrompt) asks for it.

export const DRAFT_MARKER_RE = /^-{2,}.*AI-ASSISTED DRAFT.*-{2,}\s*$/i;
const TITLE_LINE_RE = /^[*_#\s]*document(?:\s+title)?\s*:\s*(.+?)[*_\s]*$/i;

/** Persisted-only rows: never shown as chat bubbles. */
export const SNAPSHOT_MARKER = (source: 'ai' | 'manual') => `--- ED DOCUMENT SNAPSHOT (${source}) ---`;
const SNAPSHOT_RE = /^--- ED DOCUMENT SNAPSHOT \((ai|manual)\) ---$/;
export const APPROVED_MARKER = '--- ED DOCUMENT APPROVED ---';
export const DISCARDED_MARKER = '--- ED DOCUMENT DISCARDED ---';
/** Sent as doc_type when no document is open yet (the AI picks one). */
export const GENERIC_TITLE = 'ED Document';

/** Same as backend/utils/erDocuments.js — keep the two in step. */
export const MAX_TITLE = 40;
export const COMMON_TITLES = ['ED Discharge Summary', 'Referral Letter', 'ED Consultation Note'] as const;

export function normalizeTitle(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let t = String(raw).replace(/[*_`#"“”]/g, '').replace(/\s+/g, ' ').trim();
  const known = COMMON_TITLES.find((c) => c.toLowerCase() === t.toLowerCase());
  if (known) return known;
  if (!t) return null;
  if (t.length > MAX_TITLE) t = t.slice(0, MAX_TITLE).replace(/\s+\S*$/, '').trim() || t.slice(0, MAX_TITLE);
  return t;
}

export const titleKey = (title: string) => title.trim().toLowerCase();

export interface ParsedReply {
  /** What the AI said to the doctor. */
  chat: string;
  /** The document's title, when the reply names one. */
  title: string | null;
  /** The document text (uncleaned), or null when the reply is just an answer. */
  docText: string | null;
}

function takeTitle(lines: string[]): { title: string | null; rest: string[] } {
  let i = 0;
  while (i < lines.length && !lines[i]!.trim()) i++;
  const m = i < lines.length ? TITLE_LINE_RE.exec(lines[i]!.trim()) : null;
  if (!m) return { title: null, rest: lines };
  return { title: normalizeTitle(m[1]), rest: lines.slice(i + 1) };
}

export function parseEdReply(text: string): ParsedReply {
  const lines = (text || '').split('\n');
  const markerAt = lines.findIndex((l) => DRAFT_MARKER_RE.test(l.trim()));
  if (markerAt !== -1) {
    const { title, rest } = takeTitle(lines.slice(markerAt + 1));
    return { chat: lines.slice(0, markerAt).join('\n').trim(), title, docText: rest.join('\n') };
  }
  // Fallback for a reply that forgot the marker but did name a document on
  // its own line within the first few lines — still a document, not chat.
  const head = lines.slice(0, 4);
  const at = head.findIndex((l) => TITLE_LINE_RE.test(l.trim()));
  if (at !== -1) {
    const { title, rest } = takeTitle(lines.slice(at));
    const body = rest.join('\n').trim();
    if (title && body.length > 80) return { chat: lines.slice(0, at).join('\n').trim(), title, docText: body };
  }
  return { chat: text, title: null, docText: null };
}

/**
 * While a reply streams in: true once the line right after the draft marker
 * is complete, i.e. we know whether it is a "Document:" line and what it says.
 * Before that, a half-arrived "Doc…" would read as untitled document text and
 * get streamed into whichever tab happens to be open.
 */
export function liveDocReady(soFar: string): boolean {
  const lines = (soFar || '').split('\n');
  const markerAt = lines.findIndex((l) => DRAFT_MARKER_RE.test(l.trim()));
  if (markerAt === -1) return false;
  let i = markerAt + 1;
  while (i < lines.length && !lines[i]!.trim()) i++;
  // The first non-blank line after the marker must be followed by a newline.
  return i < lines.length - 1;
}

/** True when the reply carries a document (the transcript then hides its text). */
export const isDocumentReply = (text: string) => parseEdReply(text).docText !== null;

/**
 * Which document a request is about, when the doctor names one of the common
 * ones — used only if the AI's reply forgot its "Document:" line.
 */
export function guessTitle(request: string): string | null {
  const t = request.toLowerCase();
  if (/\bdeath\b/.test(t)) return 'Death Summary';
  if (/referr|transfer letter|refer (him|her|the patient|to)/.test(t)) return 'Referral Letter';
  if (/discharge/.test(t)) return 'ED Discharge Summary';
  if (/consult(ation)? note|case note|casualty note/.test(t)) return 'ED Consultation Note';
  return null;
}

export interface StoredMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  doc_type?: string | null;
}

export interface Draft {
  title: string;
  /** The document as it stands (what is approved). */
  text: string;
  /** The AI's last version, sent as ai_draft so edits can be measured; null for a hand-written one. */
  aiText: string | null;
  source: 'ai' | 'manual';
}

export interface Restored {
  msgs: { role: 'user' | 'assistant'; content: string }[];
  drafts: Draft[];
  activeTitle: string | null;
}

/** Rebuilds the conversation and the unapproved drafts from the stored transcript. */
export function restoreConversation(rows: StoredMessage[], clean: (doc: string) => string = (d) => cleanEdDoc(d)): Restored {
  const msgs: Restored['msgs'] = [];
  const drafts = new Map<string, Draft>();
  let activeTitle: string | null = null;
  const touch = (d: Draft) => {
    drafts.delete(titleKey(d.title));
    drafts.set(titleKey(d.title), d);
    activeTitle = d.title;
  };

  for (const r of rows) {
    const lines = (r.content || '').split('\n');
    const first = lines[0]?.trim() ?? '';
    const snap = r.role === 'assistant' ? SNAPSHOT_RE.exec(first) : null;
    if (snap) {
      const { title, rest } = takeTitle(lines.slice(1));
      const t = title ?? normalizeTitle(r.doc_type);
      if (!t) continue;
      const prev = drafts.get(titleKey(t));
      touch({ title: t, text: rest.join('\n').trim(), aiText: prev?.aiText ?? null, source: prev?.source ?? (snap[1] as 'ai' | 'manual') });
      continue;
    }
    if (r.role === 'assistant' && first === DISCARDED_MARKER) {
      const { title } = takeTitle(lines.slice(1));
      const t = title ?? normalizeTitle(r.doc_type);
      if (t) {
        drafts.delete(titleKey(t));
        if (activeTitle && titleKey(activeTitle) === titleKey(t)) activeTitle = null;
      }
      continue;
    }
    if (r.role === 'assistant' && first === APPROVED_MARKER) {
      const { title } = takeTitle(lines.slice(1));
      const t = title ?? normalizeTitle(r.doc_type);
      if (t) {
        drafts.delete(titleKey(t));
        if (activeTitle && titleKey(activeTitle) === titleKey(t)) activeTitle = null;
        msgs.push({ role: 'assistant', content: `✅ **${t}** approved and filed in the patient's record.` });
      }
      continue;
    }
    msgs.push({ role: r.role, content: r.content });
    if (r.role === 'assistant') {
      const p = parseEdReply(r.content);
      const t = p.title ?? normalizeTitle(r.doc_type);
      if (p.docText !== null && t) {
        const text = clean(p.docText);
        touch({ title: t, text, aiText: text, source: 'ai' });
      }
    }
  }
  const list = [...drafts.values()];
  if (!activeTitle && list.length) activeTitle = list[list.length - 1]!.title;
  return { msgs, drafts: list, activeTitle };
}

/** The content persisted for a hand-edit or a hand-written draft (autosave). */
export function snapshotContent(d: Draft): string {
  return `${SNAPSHOT_MARKER(d.source)}\nDocument: ${d.title}\n${d.text}`;
}

export function approvedContent(title: string): string {
  return `${APPROVED_MARKER}\nDocument: ${title}`;
}

export function discardedContent(title: string): string {
  return `${DISCARDED_MARKER}\nDocument: ${title}`;
}

/**
 * Tidies a document the AI wrote. Deliberately lighter than
 * lib/aiDraftCleanup.ts's cleanAIDraft, which drops every line before the
 * first bold heading — that would delete a referral letter's addressee
 * ("To, The Medical Officer, …"). The marker and the Document line already
 * separate the document from the chat here, so only the marker itself,
 * trailing sign-offs and surrounding blank lines go.
 */
export function cleanEdDoc(text: string, today: Date = new Date()): string {
  const lines = (text || '').split('\n').filter((l) => !DRAFT_MARKER_RE.test(l.trim()));
  const signOff = /^(please (review|let)|let me know|feel free to|does this|is there anything else)/i;
  while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();
  while (lines.length && signOff.test(lines[lines.length - 1]!.trim())) {
    lines.pop();
    while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();
  }
  while (lines.length && !lines[0]!.trim()) lines.shift();
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = `${String(today.getDate()).padStart(2, '0')} ${MONTHS[today.getMonth()]} ${today.getFullYear()}`;
  return lines.join('\n').replace(/\[today'?s date\]/gi, date);
}
